import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Access, AccessRequests } from '@/api/accesses/models';
import { ValidationErrorType, UUID, PartialRecord } from '@/types';
import { AccessType } from '@/api/__generated__/webApi';
import { createRequestAccess } from './api';

type TFetching = 'accesses' | 'accessesRequests';

export interface AccountState {
  accesses: Access | null;
  accessesRequests: AccessRequests | null;
  fetching: PartialRecord<TFetching, boolean> | null;
  error?: ValidationErrorType | null;
}
const initialState: AccountState = {
  accesses: null,
  fetching: null,
  accessesRequests: null,
};

export const accesses = createSlice({
  name: 'accesses',
  initialState,
  reducers: {
    requestAccesses() {},
    requestAccessesRequests() {},
    approveAccessesRequest(
      _state,
      _action: PayloadAction<{ requestId: UUID }>
    ) {},
    declineAccessesRequest(
      _state,
      _action: PayloadAction<{ requestId: UUID }>
    ) {},
    deleteAccessesRequest(
      _state,
      _action: PayloadAction<{ requestId: UUID }>
    ) {},
    deleteAccess(
      _state,
      _action: PayloadAction<{ accessId: UUID; type: AccessType }>
    ) {},
    setAccesses(state, { payload }: PayloadAction<{ accesses: Access }>) {
      state.accesses = payload.accesses;
    },
    setAccountAccesses(
      state,
      {
        payload,
      }: PayloadAction<{
        incomingAccountAccesses: Access['incomingAccountAccesses'];
        outgoingAccountAccesses: Access['outgoingAccountAccesses'];
      }>
    ) {
      const { incomingAccountAccesses, outgoingAccountAccesses } = payload;
      state.accesses = {
        companyAccesses: state.accesses ? state.accesses.companyAccesses : [],
        incomingAccountAccesses,
        outgoingAccountAccesses,
      };
    },
    setCompanyAccess(
      state,
      {
        payload,
      }: PayloadAction<{
        companyAccesses: Access['companyAccesses'];
      }>
    ) {
      const { companyAccesses } = payload;
      state.accesses = {
        incomingAccountAccesses: state.accesses?.incomingAccountAccesses ?? [],
        outgoingAccountAccesses: state.accesses?.outgoingAccountAccesses ?? [],
        companyAccesses,
      };
    },
    setAccessesRequests(
      state,
      { payload }: PayloadAction<{ accessesRequests: AccessRequests }>
    ) {
      state.accessesRequests = payload.accessesRequests;
    },
    setFetching(state, { payload }: PayloadAction<AccountState['fetching']>) {
      if (!payload) {
        state.fetching = null;
      }
      state.fetching = { ...state.fetching, ...payload };
    },
    setCommonError(
      state,
      { payload }: PayloadAction<ValidationErrorType | null>
    ) {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createRequestAccess.fulfilled, (state, action) => {
      state.accessesRequests?.outgoing.unshift(action.payload);
    });
  },
});

export const accessesReducer = accesses.reducer;
export const accessesActions = accesses.actions;
export const accessesStoreName = accesses.name;
